import { useEffect, useState } from "react";
import { ShoppingBag, ArrowUp } from "lucide-react";
import { useCart } from "@/lib/cart";
import { useCurrency } from "@/lib/currency";
import { DISCOUNT_TIERS } from "@/lib/discountTiers";

/**
 * Sticky bottom bar with product image/name/price + Add-to-cart.
 * Appears (slides up) once the user scrolls past the threshold so they always
 * know which product they're viewing and can buy without scrolling back up.
 *
 * Also renders a circular "scroll to top" button on the right side.
 */
export const StickyBuyBar = ({ product, qty, total, onAdd }) => {
  const [show, setShow] = useState(false);
  const { cart } = useCart();
  const { formatPrice } = useCurrency();

  useEffect(() => {
    const onScroll = () => setShow(window.scrollY > 720);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const image = (product.images && product.images[0]) || product.primary_image;
  const unit = Number(product.price || 0);

  // Active reward tier — from the user's actual cart, not the PDP qty selector
  const activeTier =
    cart?.cart_discount_pct > 0
      ? DISCOUNT_TIERS.find((t) => t.pct === cart.cart_discount_pct)
      : null;

  return (
    <>
      {/* Sticky buy bar — slides up from the bottom once scrolled */}
      <div
        className={`fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 shadow-[0_-4px_20px_rgba(0,0,0,0.06)] transform transition-transform duration-300 ${
          show ? "translate-y-0" : "translate-y-full"
        }`}
        data-testid="sticky-buy-bar"
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3 sm:gap-4">
          <img
            src={image}
            alt={product.name}
            loading="lazy"
            decoding="async"
            className="w-11 h-11 sm:w-12 sm:h-12 object-cover bg-neutral-100 shrink-0"
            data-testid="sticky-product-image"
          />
          <div className="min-w-0 flex-1">
            <div className="font-heading text-sm sm:text-base font-black tracking-tight truncate" data-testid="sticky-product-name">
              {product.name}
            </div>
            <div className="text-[10px] sm:text-xs text-neutral-500 truncate flex items-center gap-2">
              <span>{formatPrice(unit)} · {qty} unit{qty !== 1 ? "s" : ""}</span>
              {activeTier && (
                <span
                  className="sm:hidden inline-flex items-center gap-1 bg-amber-50 border border-amber-300 text-amber-800 px-1.5 py-0.5 text-[9px] tracking-[0.1em] uppercase font-bold whitespace-nowrap"
                  data-testid="sticky-tier-badge-mobile"
                >
                  {activeTier.tone} {activeTier.pct}% OFF
                </span>
              )}
            </div>
          </div>

          {activeTier && (
            <div
              className="hidden sm:inline-flex items-center gap-1.5 bg-amber-50 border border-amber-300 text-black px-2.5 py-1.5 text-[10px] tracking-[0.15em] uppercase font-bold whitespace-nowrap"
              data-testid="sticky-tier-badge"
              title={`Active reward tier · ${cart.total_qty} items in cart`}
            >
              <span className="text-sm leading-none">{activeTier.tone}</span>
              <span>{activeTier.label}</span>
              <span className="text-amber-700">· {activeTier.pct}% OFF</span>
            </div>
          )}

          <button
            onClick={() => onAdd(product, qty)}
            className="h-11 px-4 sm:px-6 bg-black text-white text-sm font-medium hover:bg-neutral-800 inline-flex items-center justify-center gap-2 whitespace-nowrap"
            data-testid="sticky-add-to-cart"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Add to cart · </span>
            <span>${total.toFixed(2)}</span>
          </button>
        </div>
      </div>

      {/* Scroll-to-top */}
      <button
        type="button"
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        className={`fixed right-4 sm:right-6 z-50 w-11 h-11 bg-black text-white border border-black rounded-full shadow-lg hover:bg-white hover:text-black transition-all ${
          show
            ? "bottom-[90px] sm:bottom-[96px] opacity-100 translate-y-0 pointer-events-auto"
            : "bottom-4 opacity-0 translate-y-3 pointer-events-none"
        }`}
        aria-label="Scroll to top"
        data-testid="scroll-to-top-btn"
      >
        <ArrowUp className="w-4 h-4 mx-auto" />
      </button>
    </>
  );
};
